export interface Wallpaper {
  id: string;
  sku: string;
  name: string;
  price: number;
  image: string;
  category: string;
  roomType: string;
  spaces: string[];
  collection: string;
  surface: 'Glossy' | 'Matte';
  mood: string;
  color: string;
  wallpaperType: 'Concept' | 'Pattern';
  collectionType: 'Standard' | 'Premium';
  slug?:string;
}

export interface CartItem extends Wallpaper {
  quantity: number;
}

export interface Review {
  id: string;
  customerName: string;
  text: string;
  image: string;
  rating: number;
}

export enum Page {
  Home = 'home',
  Listing = 'listing',
  PDP = 'pdp',
  Custom = 'custom',
  CustomUpload = 'custom-upload',
  CustomDesign = 'custom-design',
  Wishlist = 'wishlist',
  Cart = 'cart',
}

export interface Category {
  id: number;
  name: string;
  display_name: string;
  slug: string;
  count: number;
  status: number;
  description?: string | null;
  image?: string | null;
}

export interface SpaceCategory {
  id: number;
  name: string;
  display_name?: string;
  slug?: string;
  count?: number;
  image?: string | null;
}

export interface ApiListResponse<T> {
  meta: {
    pageNumber: string;
    pageSize: string;
    recordsTotal: number;
    recordsFiltered: number;
    message: string | null;
  };
  data: T[];
}

// ── Search API (betaapi.indiapicture.in) ────────────────────────────────────
export interface SearchWallpaper {
  id: number;
  wallpaper_id: string;
  slug: string;
  name: string;
  /**
   * The live "from" ₹/sq ft, tax-inclusive — the cheapest priced surface.
   *
   * NOT what Solr indexed. WallpaperService.searchWallpapers() overwrites the
   * indexed value, which is the old per-design `wallpaper.price` snapshot (₹99,
   * ₹149) from before we charged by paper. Nullable because a "from" price only
   * exists while some surface can be priced, and every consumer must render that
   * case as no price rather than ₹0.
   */
  price: number | null;
  list_thumbnail: string;
  hover_thumbnail: string;
  wallpaper_type: 'concept' | 'pattern' | string;
  collection: number;
  collection_slug: string | null;
  caption: string;
  roll_size: string;
  default_rolls: number;
  thumbnail_space1_id: number | null;
  thumbnail_space2_id: number | null;
  thumbnail_space3_id: number | null;
  space1: string | null;
  space2: string | null;
  space3: string | null;
  space4: string | null;
  space5: string | null;
  space6: string | null;
  colors: any[];
  max_color: string | null;
  min_color: string | null;
  created_at: string;
}

export interface SearchFacets {
  themes: Record<string, number>;
  spaces: Record<string, number>;
  qualities: Record<string, number>;
  types: Record<string, number>;
  max_colors: Record<string, number>;
  vastu: Record<string, number>;
}

export interface SearchWallpaperResponse {
  total: number;
  results: SearchWallpaper[];
  facet_field: SearchFacets;
}

export interface MasterItem {
  id: number;
  name: string;
  slug?: string;
  display_name?: string;
  hex?: string;
  count?: number;
  image?: string | null;
}

export interface MastersResponse {
  space: MasterItem[];
  'wallpaper-collection': MasterItem[];
  'wallpaper-theme': MasterItem[];
  color: MasterItem[];
  category: MasterItem[];
  'sub-category': MasterItem[];
}

export interface JourneyContext {
  selected_space: string | null;
  selected_space_id: number | null;
  selected_category: string | null;
  selected_category_id: number | null;
  selected_subcategory: string | null;
  selected_mood: string | null;
  interaction_history: string[];
  entry_point: 'space' | 'category' | null;
  search_query: string | null;
  nur_stage?: number;
  nur_rejected_ids?: string[];
  nur_shortlist_ids?: string[];
  nur_colors?: string[];
  nur_pattern?: string | null;
}

// ── PDP URL builder ──────────────────────────────────────────────────────────
// Builds: /wallpaper/{collection_slug}/{slug}/{max_color}-{min_color}
// Falls back to: /pdp/{slug|id} for items without collection_slug
export function buildPdpUrl(wp: Pick<SearchWallpaper, 'id' | 'slug' | 'collection_slug' | 'max_color' | 'min_color'>): string {
  if (wp.collection_slug && wp.slug) {
    const colors = `${wp.max_color || '0'}-${wp.min_color || '0'}`;
    return `/wallpaper/${wp.collection_slug}/${wp.slug}/${colors}`;
  }
  return `/pdp/${wp.slug || wp.id}`;
}

export class WallArtCategory {
  id: number = 0;
  slug: string = '';
  title: string = '';
  keyword: string = '';
  collection: string = '';
  cat_id: number | null = null;
  subcat_slug: string = '';
  thumbnail: string = '';
  status: number = 1;
}

export interface NurSignal {
  type:
    | 'listing-initial-browse'
    | 'plp-scroll-nudge'
    | 'plp-deep-scroll'
    | 'plp-behavior-pattern'
    | 'pdp-dwell'
    | 'product-click'
    | 'rapid-product-switching'
    | 'space-selected'
    | 'category-selected'
    | 'pdp-back-to-listing'
    | 'wishlist-add'
    | 'pdp-add-to-cart'
    | 'home-idle'
    | 'listing-filter-change';
  data?: {
    space?: string;
    category?: string;
    wallpaperId?: string;
    patternStyle?: string;
  };
}

export const INITIAL_CONTEXT: JourneyContext = {
  selected_space: null,
  selected_space_id: null,
  selected_category: null,
  selected_category_id: null,
  selected_subcategory: null,
  selected_mood: null,
  interaction_history: [],
  entry_point: null,
  search_query: null,
  nur_stage: 0,
  nur_rejected_ids: [],
  nur_shortlist_ids: [],
  nur_colors: [],
  nur_pattern: null,
};
